import React from 'react';
import { observer } from 'mobx-react';
import GenerateTransactionsVM from '../Views/GenerateTransactionsVM';
import { Neo } from '@singularsystems/neo-react';
import { TextConstants } from '../../../Common/TextConstants';
import { ModalUtils, Validation } from '@singularsystems/neo-core';
import InfoLabelHover from '../../../Common/Components/InfoLabelHover/InfoLabelHover';

interface IGenerateNewManualTransactionComponentProps {
    viewModel: GenerateTransactionsVM;
    onBack: () => void;
    onSave: () => void;
}

@observer
export default class GenerateNewManualTransactionComponent extends React.Component<IGenerateNewManualTransactionComponentProps> {

    public render() {
        const viewModel = this.props.viewModel;

        return (
            <>
                <Neo.Card title={TextConstants.Titles.GenerateNewTransaction} className="m-1">
                    <Neo.Form
                        model={viewModel.transaction}
                        validationDisplayMode={Validation.DisplayMode.AfterSubmit}
                        onInvalid={this.onFormInvalid}
                        onSubmit={() => this.props.onSave()}>
                        <div className="row">
                            <div className="col-lg-2 col-md-3 pt-3">
                                <Neo.RadioList
                                    bind={viewModel.transaction.meta.isCashInd}
                                    radioList={{
                                        items: [
                                            { id: false, text: TextConstants.Labels.Security },
                                            { id: true, text: TextConstants.Labels.Cash }],
                                        inline: true
                                    }}>
                                </Neo.RadioList>
                            </div>
                            <div className="col-lg-2 col-md-2">
                                <Neo.FormGroup
                                    bindContext={viewModel.transaction.meta.serviceProviderGroupingName}
                                    label={TextConstants.Labels.Broker}>
                                    <Neo.AutoCompleteDropDown
                                        itemSource={viewModel.serviceProviderApiClient.getServiceProviderFileGroupingListByCriteria}
                                        bind={viewModel.transaction.meta.staticServiceProviderFileGroupingID}
                                        bindDisplay={viewModel.transaction.meta.serviceProviderGroupingName}
                                        displayMember={"serviceProviderGroupingName"} 
                                        items={viewModel.serviceProviderApiClient.getServiceProviderFileGroupingListByCriteria(" ").then((arr) => arr.data.slice(0,10)
                                        .sort((x,y)=> x.serviceProviderGroupingName.localeCompare(y.serviceProviderGroupingName))
                                        )}/>
                                </Neo.FormGroup>
                            </div>
                            <div className="col-lg-2 col-md-2">
                                <Neo.FormGroup
                                    bindContext={viewModel.transaction.meta.rawAccountID}
                                    label={TextConstants.Labels.AccountNumber}>
                                    <Neo.AutoCompleteDropDown
                                        itemSource={(searchValue) => viewModel.clientAccountApiClient.getClientAccountNumbersListByCriteria(searchValue, viewModel.transaction.meta.staticServiceProviderFileGroupingID.value)}
                                        bind={viewModel.transaction.meta.clientAccountID}
                                        bindDisplay={viewModel.transaction.meta.rawAccountID}
                                        displayMember={"serviceProviderAccountNumber"} 
                                        isReadOnly={viewModel.transaction.meta.staticServiceProviderFileGroupingID.value === 0}
                                        items={viewModel.clientAccountApiClient.getClientAccountNumbersListByCriteria(" ", viewModel.transaction.meta.staticServiceProviderFileGroupingID.value).then((arr) => arr.data.slice(0,10)
                                        
                                        )}/>
                                    </Neo.FormGroup>
                            </div>
                        </div>
                        <Neo.GridLayout lg={3} md={3}>
                            <Neo.FormGroup
                                label={TextConstants.Labels.Instrument}
                                bindContext={viewModel.transaction.meta.securityCode}>
                                <Neo.AutoCompleteDropDown
                                    itemSource={viewModel.instrumentApiCient.getInstrumentLookupListByCriteria}
                                    bind={viewModel.transaction.meta.staticInstrumentID}
                                    bindDisplay={viewModel.transaction.meta.ISIN}
                                    option={item => <div>
                                        <div style={{ flexBasis: 200 }}>{item.description}</div>
                                        <div style={{ flexBasis: 200 }}><small className="text-muted">{item.isin}</small></div>
                                    </div>}
                                    onItemSelected={() => viewModel.populateInstrumentDetails(viewModel.transaction.staticInstrumentID)} 
                                    items={viewModel.instrumentApiCient.getInstrumentLookupListByCriteria(" ").then((arr) => arr.data.slice(0,10)
                                    .sort((x,y)=> x.isin!.localeCompare(y.isin!))
                                    )}/>
                            </Neo.FormGroup>
                            <Neo.FormGroup
                                label={TextConstants.Labels.Event}
                                bindContext={viewModel.transaction.meta.eventType}>
                                <Neo.AutoCompleteDropDown
                                    itemSource={viewModel.eventApiClient.getEventLookupListByCriteria}
                                    bind={viewModel.transaction.meta.staticEventID}
                                    bindDisplay={viewModel.transaction.meta.eventType}
                                    displayMember={"staticEventName"} 
                                    onItemSelected={() => viewModel.populateEventDetails(viewModel.transaction.staticEventID)} 
                                    items={viewModel.eventApiClient.getEventLookupListByCriteria(" ").then((arr) => arr.data.slice(0,10)
                                    .sort((x,y)=> x.staticEventName.localeCompare(y.staticEventName))
                                    )}/>
                            </Neo.FormGroup>
                            <Neo.FormGroup
                                label={TextConstants.Labels.ExchangeName}
                                bindContext={viewModel.transaction.meta.exchangeMIC}>
                                <Neo.AutoCompleteDropDown
                                    itemSource={viewModel.exchangeApiClient.getExchangeLookupListByCriteria}
                                    bind={viewModel.transaction.meta.exchangeID}
                                    bindDisplay={viewModel.transaction.meta.exchangeMIC}
                                    option={item => <div>
                                        <div style={{ flexBasis: 200 }}>{item.mic}</div>
                                        <div style={{ flexBasis: 200 }}><small className="text-muted">{item.exchangeName}</small></div>
                                    </div>
                                    }
                                    onItemSelected={() => viewModel.populateExchangeDetails(viewModel.transaction.exchangeID)} 
                                    items={viewModel.exchangeApiClient.getExchangeLookupListByCriteria(" ").then((arr) => arr.data.slice(0,10)
                                    .sort((x,y)=> x.exchangeName.localeCompare(y.exchangeName))
                                    )}/>
                            </Neo.FormGroup>
                        </Neo.GridLayout>
                        <Neo.GridLayout lg={6} md={6}>
                            <Neo.FormGroup
                                label={TextConstants.Labels.TransactionDate}
                                bind={viewModel.transaction.meta.dateTimeEvent}>
                            </Neo.FormGroup>
                            <Neo.FormGroup
                                label={TextConstants.Labels.ValueDate}
                                bind={viewModel.transaction.meta.dateValue}>
                            </Neo.FormGroup>
                            <Neo.FormGroup
                                label={TextConstants.Labels.InstrumentCurrency}
                                bind={viewModel.transaction.meta.accountCurrencyCode}
                                select={{ items: viewModel.instrumentCurrencyLookupList, displayMember: "currencyCode", valueMember: "currencyCode" }}
                                />
                            <Neo.FormGroup
                                label={TextConstants.Labels.AccountCurrency}
                                bind={viewModel.transaction.meta.instrumentCurrencyCode}
                                select={{ items: viewModel.accountCurrencyLookupList, displayMember: "currencyCode", valueMember: "currencyCode"}}
                                />
                            <Neo.FormGroup
                                bind={viewModel.transaction.meta.transactionQuantity}
                                label={TextConstants.Labels.TransactionQuantity} />
                            <Neo.FormGroup
                                bind={viewModel.transaction.meta.transactionAmount}
                                label={TextConstants.Labels.TransactionAmount} />
                        </Neo.GridLayout>
                        <div className="row">
                            <div className="col-lg-4">
                                <Neo.FormGroup
                                    bind={viewModel.transaction.meta.comment}
                                    input={{ rows: 2 }} />
                            </div>
                            <div className="col-lg-2">
                                <Neo.FormGroup
                                    bind={viewModel.transaction.meta.eventID}
                                    label={<InfoLabelHover title={TextConstants.Labels.EventID} content={TextConstants.Tooltips.EventIdInfo} />} />
                            </div>
                            <div className="col-lg-2">
                                <Neo.FormGroup
                                    bind={viewModel.transaction.meta.eventIDRelated}
                                    label={TextConstants.Labels.RelatedTrasactionID} />
                            </div>
                            <div className="col-lg-2 pt-4">
                                <Neo.FormGroup
                                    bind={viewModel.transaction.meta.isReversal}
                                    label={TextConstants.Labels.Reversal} />
                            </div>
                        </div>
                        <div className="row customMarginTop29 float-right pr-3">
                            <Neo.Button
                                variant="primary" className="ml-3 col-md-2" isSubmit
                            >{TextConstants.Buttons.Save}</Neo.Button>
                            <Neo.Button className="ml-3 col-md-2"
                                onClick={() => this.props.onBack()}
                                variant="secondary"
                            >{TextConstants.Buttons.Cancel}</Neo.Button>
                        </div>
                    </Neo.Form>
                </Neo.Card>
            </>
        );
    }
    private onFormInvalid(e: React.FormEvent, justValidated?: boolean) {
        if (!justValidated) {
            ModalUtils.showMessage("Save", TextConstants.Validation.InvalidTransaction);
        }
    }
}